import React, { useEffect, useState, useMemo } from "react";
import { Portal } from "@/lib/api";
import { PlayCircle, Search } from "lucide-react";
import VideoModal from "@/components/VideoModal";
import { toast } from "sonner";

const PortalVideos = () => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("All");
  const [playing, setPlaying] = useState(null);

  useEffect(() => {
    (async () => {
      try { setVideos(await Portal.videos.list()); }
      catch (e) { toast.error(e?.response?.data?.detail || "Failed to load videos"); }
      finally { setLoading(false); }
    })();
  }, []);

  const categories = useMemo(() => {
    const set = new Set(videos.map((v) => v.category || "General"));
    return ["All", ...Array.from(set).sort()];
  }, [videos]);

  const filtered = useMemo(() => {
    const needle = q.toLowerCase().trim();
    return videos.filter((v) => {
      if (category !== "All" && (v.category || "General") !== category) return false;
      if (!needle) return true;
      return (v.title + " " + v.description).toLowerCase().includes(needle);
    });
  }, [videos, q, category]);

  return (
    <section className="p-10" data-testid="portal-videos-tab">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.22em] text-atc-blue mb-2">Portal</div>
        <h1 className="font-display font-black text-4xl text-white">Training Videos</h1>
        <p className="text-slate-400 text-sm mt-2">Watch step-by-step training and product walk-throughs from ATC.</p>
      </div>

      <div className="mb-6 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search videos…"
            className="w-full pl-10 pr-4 py-2.5 rounded-lg bg-slate-950 border border-white/10 text-sm text-white placeholder:text-slate-500"
            data-testid="portal-video-search"
          />
        </div>
        <div className="flex flex-wrap gap-1.5">
          {categories.map((c) => (
            <button
              key={c}
              onClick={() => setCategory(c)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition ${
                category === c
                  ? "bg-atc-blue text-white"
                  : "bg-slate-900/60 text-slate-300 border border-white/10 hover:border-white/25"
              }`}
              data-testid={`portal-video-cat-${c}`}
            >
              {c}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-slate-400">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="glass rounded-xl p-12 text-center text-slate-400" data-testid="portal-videos-empty">
          No videos here yet. Check back soon.
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {filtered.map((v) => (
            <button
              key={v.id}
              type="button"
              onClick={() => setPlaying(v)}
              className="text-left glass rounded-2xl overflow-hidden border border-white/5 hover:border-atc-blue/40 transition group"
              data-testid={`portal-video-card-${v.id}`}
            >
              <div className="relative aspect-video bg-slate-950 flex items-center justify-center overflow-hidden">
                {v.thumbnail_url ? (
                  <img src={v.thumbnail_url} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="absolute inset-0 bg-gradient-to-br from-atc-blue/20 to-atc-green/10" />
                )}
                <PlayCircle className="relative h-14 w-14 text-white/90 drop-shadow-lg group-hover:scale-110 transition" />
                {v.duration && (
                  <span className="absolute bottom-2 right-2 px-2 py-0.5 bg-slate-950/80 text-white text-xs rounded">{v.duration}</span>
                )}
              </div>
              <div className="p-4">
                <div className="text-[10px] uppercase tracking-[0.18em] text-atc-blue mb-1.5">{v.category || "General"}</div>
                <div className="text-white text-sm font-semibold leading-snug">{v.title}</div>
                {v.description && <div className="text-xs text-slate-400 mt-1.5 line-clamp-2">{v.description}</div>}
              </div>
            </button>
          ))}
        </div>
      )}

      {playing && (
        <VideoModal url={playing.video_url} title={playing.title} />
      )}
    </section>
  );
};

export default PortalVideos;
